import { AnalyticsData, createDefaultAnalyticsData } from '../types/analytics';
import { databaseService } from './DatabaseService';
import { TimeZoneService } from './TimeZoneService';
import logger from '@/utils/logger';

/**
 * Service for tracking and analyzing user behavior within the application
 * Follows SOLID principles with single responsibility for analytics management
 */
class AnalyticsService {
  private static readonly ANALYTICS_COLLECTION = 'user_analytics';
  private static readonly ANALYTICS_ID = 'main';
  private static readonly APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';

  private data: AnalyticsData | null = null;
  private sessionStartTime: number = 0;
  private actionsInCurrentSession: number = 0;
  private initialized: boolean = false;
  private timezoneService: TimeZoneService;

  constructor() {
    this.timezoneService = new TimeZoneService();
    this.initializeAnalytics().catch(err => {
      logger.error('[AnalyticsService] Initialization error:', err);
    });

    // Set up event listeners for session tracking
    this.setupSessionTracking();
  }

  /**
   * Initialize analytics data, creating default data if none exists
   */
  private async initializeAnalytics(): Promise<void> {
    if (this.initialized) return;

    try {
      await databaseService.initialize();
      
      // Try to load existing analytics data
      const existingData = await databaseService.getData(
        AnalyticsService.ANALYTICS_COLLECTION,
        AnalyticsService.ANALYTICS_ID
      );

      if (existingData) {
        // Ensure we have all required fields from the analytics data type
        const defaultData = createDefaultAnalyticsData();
        const typedData = existingData as Partial<AnalyticsData>;
        
        this.data = {
          ...defaultData,
          ...typedData,
          // Ensure nested objects are properly merged
          visitsPerTimeOfDay: { 
            ...defaultData.visitsPerTimeOfDay, 
            ...(typedData.visitsPerTimeOfDay || {}) 
          },
          visitsPerDayOfWeek: { 
            ...defaultData.visitsPerDayOfWeek, 
            ...(typedData.visitsPerDayOfWeek || {}) 
          },
          featureUsage: { 
            ...defaultData.featureUsage, 
            ...(typedData.featureUsage || {}) 
          },
          preferences: { 
            ...defaultData.preferences, 
            ...(typedData.preferences || {}) 
          },
        };
        // Skip analytics data logging to reduce console noise
      } else {
        // Create new analytics data if none exists
        this.data = createDefaultAnalyticsData();
        // Skip analytics data logging to reduce console noise
      }

      // Update with current app version
      this.data.lastSeenAppVersion = this.data.currentAppVersion;
      this.data.currentAppVersion = AnalyticsService.APP_VERSION;
      
      // Check if update notification should be shown
      this.data.updateNotificationShown = 
        this.data.lastSeenAppVersion === this.data.currentAppVersion;

      // Start a new session
      this.startNewSession();
      
      // Record this visit
      await this.recordVisit();
      
      this.initialized = true;
    } catch (error) {
      
      throw error;
    }
  }

  /**
   * Save analytics data to persistent storage
   * Implements data pruning to prevent storage size issues
   */
  private async saveAnalytics(): Promise<void> {
    if (!this.data) return;

    try {
      // Prune data before saving to prevent size issues
      const prunedData = this.pruneAnalyticsData(this.data);
      
      await databaseService.saveData(
        AnalyticsService.ANALYTICS_COLLECTION,
        AnalyticsService.ANALYTICS_ID,
        prunedData
      );
      // Skip analytics save logging to reduce console noise
    } catch (error) {
      
      
      // If error is related to string length, try saving with more aggressive pruning
      if (error instanceof RangeError && (error.message?.includes('string length') || error.message?.includes('too large'))) {
        
        try {
          // Create minimal analytics data with only critical fields
          const baseData = createDefaultAnalyticsData();
          const minimalData = {
            ...baseData,
            ...this.data,
            // Keep only the most essential metrics and reset growing collections
            totalVisits: this.data.totalVisits,
            totalMedicationsAdded: this.data.totalMedicationsAdded,
            totalMedicationsTaken: this.data.totalMedicationsSkipped,
            totalMedicationsSkipped: this.data.totalMedicationsSkipped,
            visitsPerTimeOfDay: this.data.visitsPerTimeOfDay,
            visitsPerDayOfWeek: this.data.visitsPerDayOfWeek,
            visitsPerWeek: {},
            visitsPerMonth: {},
            visitsPerYear: { [new Date().getFullYear().toString()]: this.data.totalVisits },
            sessionsData: [],
            // Ensure Plus tier feature names are updated (not Pro)
            featureUsage: {
              ...baseData.featureUsage,
              ...this.data.featureUsage
            }
          };
          
          await databaseService.saveData(
            AnalyticsService.ANALYTICS_COLLECTION,
            AnalyticsService.ANALYTICS_ID,
            minimalData
          );
          logger.info('[AnalyticsService] Successfully saved minimal analytics data');
        } catch (fallbackError) {
          
        }
      }
    }
  }

  /**
   * Data pruning utility to limit the size of analytics data
   * @param data The analytics data to prune
   * @returns Pruned analytics data object
   */
  private pruneAnalyticsData(data: AnalyticsData): AnalyticsData {
    // Create a copy to avoid modifying the original
    const prunedData = { ...data };
    
    // Limit sessions data to recent entries
    if (prunedData.sessionsData && prunedData.sessionsData.length > 20) {
      prunedData.sessionsData = prunedData.sessionsData.slice(-20);
    }
    
    // Keep only recent week/month/year data
    const pruneRecords = (record: Record<string, number>, limit: number): Record<string, number> => {
      const keys = Object.keys(record).sort().slice(-limit);
      return keys.reduce((acc, key) => ({ ...acc, [key]: record[key] }), {});
    };
    
    prunedData.visitsPerWeek = pruneRecords(prunedData.visitsPerWeek || {}, 10);
    prunedData.visitsPerMonth = pruneRecords(prunedData.visitsPerMonth || {}, 10);
    prunedData.visitsPerYear = pruneRecords(prunedData.visitsPerYear || {}, 5);
    
    return prunedData;
  }
  
  /**
   * Set up event listeners for session tracking
   */
  private setupSessionTracking(): void {
    // Record action on user interaction
    document.addEventListener('click', () => this.recordAction());
    document.addEventListener('keydown', () => this.recordAction());
    document.addEventListener('scroll', () => this.recordAction(), { passive: true });

    // End session when user leaves
    window.addEventListener('beforeunload', () => this.endCurrentSession());
    
    // Handle visibility changes (tab switching)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.endCurrentSession();
      } else if (document.visibilityState === 'visible') {
        this.startNewSession();
      }
    });
  }

  /**
   * Start a new analytics session
   */
  private startNewSession(): void {
    this.sessionStartTime = Date.now();
    this.actionsInCurrentSession = 0;
    // Skip session logging to reduce console noise
  }

  /**
   * End the current analytics session and record its data
   */
  private async endCurrentSession(): Promise<void> {
    if (!this.data || this.sessionStartTime === 0) return;

    const sessionDuration = Math.floor((Date.now() - this.sessionStartTime) / 1000);
    if (sessionDuration < 2) return; // Ignore very short sessions (likely false triggers)

    // Add the session data
    if (!this.data.sessionsData) {
      this.data.sessionsData = [];
    }
    
    this.data.sessionsData.push({
      timestamp: new Date(this.sessionStartTime).toISOString(),
      durationSeconds: sessionDuration,
      actionsPerformed: this.actionsInCurrentSession
    });

    // Keep only the most recent 50 sessions to avoid excessive growth
    if (this.data.sessionsData.length > 50) {
      this.data.sessionsData = this.data.sessionsData.slice(-50);
    }

    // Recalculate average session duration
    const totalDuration = this.data.sessionsData.reduce(
      (sum, session) => sum + session.durationSeconds, 
      0
    );
    this.data.averageSessionDurationSeconds = 
      totalDuration / this.data.sessionsData.length;

    // Save the updated analytics
    await this.saveAnalytics();
    
    // Reset session tracking
    this.sessionStartTime = 0;
    logger.info('[AnalyticsService] Session ended and recorded');
  }

  /**
   * Record a user action in the current session
   */
  private recordAction(): void {
    this.actionsInCurrentSession++;
  }

  /**
   * Record a visit and update time-based distribution metrics
   */
  private async recordVisit(): Promise<void> {
    try {
      if (!this.data) {
        
        return;
      }

      // Ensure necessary data structures exist to prevent null reference errors
      if (!this.data.visitsPerTimeOfDay) {
        logger.info('[AnalyticsService] Initializing missing visitsPerTimeOfDay');
        this.data.visitsPerTimeOfDay = {
          morning: 0,
          afternoon: 0,
          evening: 0,
          night: 0
        };
      }

      if (!this.data.visitsPerDayOfWeek) {
        logger.info('[AnalyticsService] Initializing missing visitsPerDayOfWeek');
        this.data.visitsPerDayOfWeek = {
          sunday: 0,
          monday: 0,
          tuesday: 0,
          wednesday: 0,
          thursday: 0,
          friday: 0,
          saturday: 0
        };
      }

      // Initialize dictionary fields if missing
      this.data.visitsPerWeek = this.data.visitsPerWeek || {};
      this.data.visitsPerMonth = this.data.visitsPerMonth || {};
      this.data.visitsPerYear = this.data.visitsPerYear || {};

      // Update basic visit metrics
      this.data.lastVisitTimestamp = new Date().toISOString();
      this.data.totalVisits = (this.data.totalVisits || 0) + 1;

      // Get current date and time information
      const now = new Date();
      const userLocalTime = this.timezoneService.getUserLocalTime(now);
      const hours = userLocalTime.getHours();
      const dayOfWeek = userLocalTime.getDay();
    
    // Update time of day distribution
    if (hours >= 5 && hours < 12) {
      this.data.visitsPerTimeOfDay.morning++;
    } else if (hours >= 12 && hours < 17) {
      this.data.visitsPerTimeOfDay.afternoon++;
    } else if (hours >= 17 && hours < 21) {
      this.data.visitsPerTimeOfDay.evening++;
    } else {
      this.data.visitsPerTimeOfDay.night++;
    }

    // Update day of week distribution
    const dayMap = [
      'sunday', 'monday', 'tuesday', 'wednesday', 
      'thursday', 'friday', 'saturday'
    ] as const;
    this.data.visitsPerDayOfWeek[dayMap[dayOfWeek]]++;

    // Update week, month, and year distributions
    const weekKey = this.getISOWeekYear(userLocalTime);
    const monthKey = `${userLocalTime.getFullYear()}-${String(userLocalTime.getMonth() + 1).padStart(2, '0')}`;
    const yearKey = String(userLocalTime.getFullYear());

    this.data.visitsPerWeek[weekKey] = (this.data.visitsPerWeek[weekKey] || 0) + 1;
    this.data.visitsPerMonth[monthKey] = (this.data.visitsPerMonth[monthKey] || 0) + 1;
    this.data.visitsPerYear[yearKey] = (this.data.visitsPerYear[yearKey] || 0) + 1;

    // Save the updated analytics
    await this.saveAnalytics();
    } catch (error) {
      
    }
  }

  /**
   * Get ISO week and year string in YYYY-WXX format
   */
  private getISOWeekYear(date: Date): string {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
    const week = Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 4).getTime()) / 86400000 / 7) + 1;
    return `${date.getFullYear()}-W${String(week).padStart(2, '0')}`;
  }

  // PUBLIC TRACKING METHODS

  /**
   * Track medication-related events
   * @param medication - Optional medication details for advanced analytics
   * @param profileId - Optional profile ID for user segmentation
   */
  public async trackMedicationAdded(
    medication?: { name?: string; dosage?: string; timing?: string },
    profileId?: string
  ): Promise<void> {
    await this.ensureInitialized();
    if (!this.data) return;
    
    this.data.totalMedicationsAdded++;
    this.data.featureUsage.medicationAdded++;
    
    // Track additional metadata if provided
    if (medication || profileId) {
      this.trackEvent('medication_added', {
        ...medication,
        profileId,
        timestamp: new Date().toISOString()
      });
    }
    
    this.recordAction();
    await this.saveAnalytics();
  }

  /**
   * Track medication archived events
   * @param medicationId - Optional medication ID for tracking which medications are archived
   * @param profileId - Optional profile ID for user segmentation
   */
  public async trackMedicationArchived(
    medicationId?: string,
    profileId?: string
  ): Promise<void> {
    await this.ensureInitialized();
    if (!this.data) return;
    
    this.data.totalMedicationsArchived++;
    
    // Track additional metadata if provided
    if (medicationId || profileId) {
      this.trackEvent('medication_archived', {
        medicationId,
        profileId,
        timestamp: new Date().toISOString()
      });
    }
    
    this.recordAction();
    await this.saveAnalytics();
  }

  /**
   * Track when a medication is marked as taken
   * @param medicationId - Optional medication ID for tracking which medications are taken
   * @param profileId - Optional profile ID for user segmentation
   */
  public async trackMedicationTaken(
    medicationId?: string,
    profileId?: string
  ): Promise<void> {
    await this.ensureInitialized();
    if (!this.data) return;
    
    this.data.totalMedicationsTaken++;
    
    // Track additional metadata if provided
    if (medicationId || profileId) {
      this.trackEvent('medication_taken', {
        medicationId,
        profileId,
        timestamp: new Date().toISOString()
      });
    }
    
    this.recordAction();
    await this.saveAnalytics();
  }

  /**
   * Track when a medication is marked as skipped
   * @param medicationId - Optional medication ID for tracking which medications are skipped
   * @param profileId - Optional profile ID for user segmentation
   */
  public async trackMedicationSkipped(
    medicationId?: string,
    profileId?: string
  ): Promise<void> {
    await this.ensureInitialized();
    if (!this.data) return;
    
    this.data.totalMedicationsSkipped++;
    
    // Track additional metadata if provided
    if (medicationId || profileId) {
      this.trackEvent('medication_skipped', {
        medicationId,
        profileId,
        timestamp: new Date().toISOString()
      });
    }
    
    this.recordAction();
    await this.saveAnalytics();
  }

  /**
   * Track feature usage events
   * @param feature - The feature being used
   */
  public async trackFeatureUsage(feature: keyof AnalyticsData['featureUsage']): Promise<void> {
    await this.ensureInitialized();
    if (!this.data || !this.data.featureUsage.hasOwnProperty(feature)) {
      
      return;
    }
    
    this.data.featureUsage[feature]++;
    this.recordAction();
    await this.saveAnalytics();
  }
  
  /**
   * Enhanced tracking for Plus tier features with detailed usage metrics
   * @param feature - The Plus tier feature being used
   * @param subFeature - Optional specific functionality within the feature
   * @param metadata - Optional additional data about the usage
   */
  public async trackPlusFeatureUsage(
    feature: keyof AnalyticsData['featureUsage'], 
    subFeature?: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    await this.ensureInitialized();
    if (!this.data) return;
    
    // Initialize Plus tier feature tracking if it doesn't exist
    if (!this.data.plusFeatureDetails) {
      this.data.plusFeatureDetails = {};
    }
    
    // Initialize feature tracking if it doesn't exist
    if (!this.data.plusFeatureDetails[feature]) {
      this.data.plusFeatureDetails[feature] = {
        totalUsage: 0,
        lastUsed: null,
        subFeatures: {}
      };
    }
    
    const featureData = this.data.plusFeatureDetails[feature];
    
    // Update base feature metrics
    featureData.totalUsage++;
    featureData.lastUsed = new Date().toISOString();
    
    // If subfeature specified, track usage for that specific functionality
    if (subFeature) {
      if (!featureData.subFeatures[subFeature]) {
        featureData.subFeatures[subFeature] = {
          usageCount: 0,
          lastUsed: null
        };
      }
      
      featureData.subFeatures[subFeature].usageCount++;
      featureData.subFeatures[subFeature].lastUsed = new Date().toISOString();
    }
    
    // If metadata provided, store the most recent usage context
    if (metadata) {
      featureData.recentContext = metadata;
    }
    
    // Also increment the standard feature counter
    this.data.featureUsage[feature]++;
    this.recordAction();
    
    await this.saveAnalytics();
  }

  /**
   * Track a generic custom event with parameters
   * @param eventName Name of the event to track
   * @param parameters Optional parameters associated with the event
   */
  public async trackEvent(eventName: string, parameters?: Record<string, any>): Promise<void> {
    await this.ensureInitialized();
    if (!this.data) return;
    
    // Initialize custom events tracking if it doesn't exist
    if (!this.data.customEvents) {
      this.data.customEvents = {};
    }
    
    // Initialize or increment counter for this event
    this.data.customEvents[eventName] = (this.data.customEvents[eventName] || 0) + 1;
    
    // Log event parameters if in development mode
    // Use try-catch to handle environments where import.meta.env is not available (like Jest)
    const isDev = (() => {
      try {
        return import.meta.env.DEV || false;
      } catch {
        return false;
      }
    })();
    
    if (isDev) {
      logger.info(`[AnalyticsService] Event tracked: ${eventName}`, parameters || {});
    }
    
    this.recordAction();
    await this.saveAnalytics();
  }

  /**
   * Update user preference data
   */
  public async updatePreference<K extends keyof AnalyticsData['preferences']>(
    preference: K, 
    value: AnalyticsData['preferences'][K]
  ): Promise<void> {
    await this.ensureInitialized();
    if (!this.data) return;
    
    this.data.preferences[preference] = value;
    this.recordAction();
    await this.saveAnalytics();
  }

  /**
   * Ensure analytics is initialized before performing operations
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.initializeAnalytics();
    }
  }

  /**
   * Get analytics data for reporting and visualization
   */
  public async getAnalyticsData(): Promise<AnalyticsData | null> {
    await this.ensureInitialized();
    return this.data;
  }

  /**
   * Check if update notification should be shown
   */
  public async shouldShowUpdateNotification(): Promise<boolean> {
    await this.ensureInitialized();
    return this.data ? !this.data.updateNotificationShown : false;
  }

  /**
   * Mark update notification as shown
   */
  public async markUpdateNotificationShown(): Promise<void> {
    await this.ensureInitialized();
    if (!this.data) return;
    
    this.data.updateNotificationShown = true;
    await this.saveAnalytics();
  }
}

// Export as a singleton
export const analyticsService = new AnalyticsService();
